// Article éditorial /fr/guides/<slug> — un H1, réponse d'abord, module trajet, CTA contextuel, JSON-LD Article
import { useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowRight, CalendarDays, RefreshCw } from "lucide-react";
import { Breadcrumbs, JsonLd, useHreflang, useSeo } from "@/components/seo/Seo";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { AuthorBlock, GuideCard, GuideCta, GuideFacts } from "@/components/guides/GuideBlocks";
import { extractHeadings, renderMarkdown } from "@/lib/markdown";
import { trackEvent } from "@/lib/analytics";
import { BASE_URL, LANG_LABEL, p } from "@/lib/seoContent";
import {
  CLUSTERS, GUIDES_COPY, aboutUrl, clusterUrl, dateOnly, formatDate, guideAlternates, guideBySlug, guideCanonical, guideUrl, guidesHubUrl, nextReads,
} from "@/lib/guides";

export default function GuideArticle({ lang, slug }) {
  const g = guideBySlug(lang, slug);
  const c = GUIDES_COPY[lang];
  const published = g?.status === "published";
  const path = g ? guideUrl(g) : guidesHubUrl(lang);
  const alternates = g ? guideAlternates(g) : null;
  useSeo({ lang, title: g?.seo_title || g?.title || c.title, description: g?.meta_description || "", path, type: "guide", id: g?.id || "", noindex: !g || !published || Boolean(g.noindex) });
  useHreflang(published ? alternates : null);
  useEffect(() => { if (g) trackEvent("guide_view", { guide_id: g.id }); }, [g]);

  if (!g) return <Navigate to={guidesHubUrl(lang)} replace />;
  if (g.slug !== slug) return <Navigate to={guideUrl(g)} replace />;

  const cl = CLUSTERS[g.category];
  const headings = extractHeadings(g.body);
  const next = nextReads(g);
  const crumbs = [{ label: c.home, to: p(lang) }, { label: c.title, to: guidesHubUrl(lang) }, { label: cl.name[lang], to: clusterUrl(lang, g.category) }, { label: g.h1 || g.title }];
  const otherLangs = Object.entries(alternates).filter(([l]) => l !== lang);
  const jsonLd = [
    { "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: crumbs.map((x, i) => ({ "@type": "ListItem", position: i + 1, name: x.label, ...(x.to ? { item: `${BASE_URL}${x.to}` } : {}) })) },
    { "@context": "https://schema.org", "@type": "Article", headline: g.h1 || g.title, description: g.meta_description || g.summary,
      ...(g.hero_image ? { image: [`${BASE_URL}${g.hero_image}`] } : {}),
      datePublished: dateOnly(g.published_at), dateModified: dateOnly(g.updated_at || g.published_at), inLanguage: lang,
      author: { "@type": "Organization", name: c.author, url: `${BASE_URL}${aboutUrl(lang)}` },
      publisher: { "@type": "Organization", name: "MILES", url: BASE_URL, logo: { "@type": "ImageObject", url: `${BASE_URL}/brand/logo-blue.png` } },
      mainEntityOfPage: { "@type": "WebPage", "@id": guideCanonical(g) } },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="guide-article">
      <Navbar />
      <JsonLd id="guide-article" data={jsonLd} />
      <main className="mx-auto max-w-6xl px-5 pb-20 pt-24 sm:px-8 lg:pt-28">
        {!published && (
          <p className="mb-6 rounded-xl border border-amber-300 bg-amber-50 px-4 py-2 text-xs font-medium text-amber-800" data-testid="guide-draft-banner">{c.draft}</p>
        )}
        <Breadcrumbs items={crumbs} />
        <header className="max-w-3xl">
          <Link to={clusterUrl(lang, g.category)} className="font-mono text-[11px] uppercase tracking-[0.3em] text-miles hover:underline">{cl.name[lang]}</Link>
          <h1 className="mt-3 text-3xl font-bold leading-[1.1] tracking-tight text-ink sm:text-4xl lg:text-5xl" data-testid="guide-h1">{g.h1 || g.title}</h1>
          {g.summary && <p className="mt-5 text-base leading-relaxed text-slate sm:text-lg" data-testid="guide-summary">{g.summary}</p>}
          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate">
            <AuthorBlock lang={lang} compact />
            {g.published_at && <span className="inline-flex items-center gap-1.5" data-testid="guide-published"><CalendarDays className="h-3.5 w-3.5" strokeWidth={1.5} />{c.published} {formatDate(g.published_at, lang)}</span>}
            {g.updated_at && dateOnly(g.updated_at) !== dateOnly(g.published_at) && (
              <span className="inline-flex items-center gap-1.5" data-testid="guide-updated"><RefreshCw className="h-3.5 w-3.5" strokeWidth={1.5} />{c.updated} {formatDate(g.updated_at, lang)}</span>
            )}
          </div>
        </header>

        {g.hero_image && (
          <figure className="mt-10 overflow-hidden rounded-3xl">
            <img src={g.hero_image} alt={g.hero_alt || ""} width={g.hero_width || 1600} height={g.hero_height || 900} fetchPriority="high" decoding="async"
              className="aspect-[2/1] w-full object-cover sm:aspect-[21/9]" data-testid="guide-hero-image" />
          </figure>
        )}

        <div className="mt-10 grid gap-12 lg:grid-cols-[minmax(0,1fr)_280px]">
          <article className="min-w-0 max-w-3xl">
            <GuideFacts g={g} lang={lang} />
            {headings.length >= 3 && (
              <nav aria-label={c.toc} className="my-8 rounded-2xl border border-ink/10 bg-cloud/60 p-5 lg:hidden" data-testid="guide-toc-mobile">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate">{c.toc}</p>
                <ol className="mt-3 space-y-2 text-sm">{headings.map((h) => <li key={h.id}><a href={`#${h.id}`} className="text-ink hover:text-miles">{h.text}</a></li>)}</ol>
              </nav>
            )}
            <div data-testid="guide-body">{renderMarkdown(g.body)}</div>
            {g.faq?.length > 0 && (
              <section className="mt-12" data-testid="guide-faq">
                <h2 className="text-xl font-bold text-ink sm:text-2xl">{c.faq}</h2>
                <div className="mt-4 divide-y divide-ink/[0.06] rounded-2xl border border-ink/10 bg-white">
                  {g.faq.map((f) => (
                    <details key={f.q} className="group px-5 py-4">
                      <summary className="cursor-pointer list-none text-sm font-semibold text-ink transition-colors group-open:text-miles">{f.q}</summary>
                      <p className="mt-2 text-sm leading-relaxed text-slate">{f.a}</p>
                    </details>
                  ))}
                </div>
              </section>
            )}
            <GuideCta g={g} lang={lang} position="end" />
            {g.sources?.length > 0 && (
              <section className="mt-8 text-xs text-slate" data-testid="guide-sources">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em]">{c.sources}</p>
                <ul className="mt-2 space-y-1">{g.sources.map((s) => (
                  <li key={s.url}><a href={s.url} target="_blank" rel="noopener noreferrer" className="underline decoration-ink/20 underline-offset-2 hover:text-miles">{s.label}</a></li>))}</ul>
              </section>
            )}
            <div className="mt-10"><AuthorBlock lang={lang} /></div>
          </article>

          <aside className="hidden lg:block">
            <div className="sticky top-28 space-y-6">
              {headings.length >= 2 && (
                <nav aria-label={c.toc} data-testid="guide-toc">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate">{c.toc}</p>
                  <ol className="mt-3 space-y-2 border-l border-ink/10 text-sm">
                    {headings.map((h) => <li key={h.id}><a href={`#${h.id}`} className="-ml-px block border-l border-transparent pl-4 text-slate transition-colors hover:border-miles hover:text-ink">{h.text}</a></li>)}
                  </ol>
                </nav>
              )}
              {otherLangs.length > 0 && (
                <div className="text-xs text-slate" data-testid="guide-other-langs">
                  {c.other_langs} {otherLangs.map(([l, href], i) => <span key={l}>{i > 0 && " · "}<Link to={href} className="font-semibold text-miles hover:underline">{LANG_LABEL[l]}</Link></span>)}
                </div>
              )}
              <Link to={guidesHubUrl(lang)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-miles hover:underline">{c.back_hub} <ArrowRight className="h-3.5 w-3.5" /></Link>
            </div>
          </aside>
        </div>

        {next.length > 0 && (
          <section className="mt-20 border-t border-ink/[0.06] pt-12" data-testid="guide-next">
            <h2 className="text-xl font-bold text-ink sm:text-2xl">{c.read_next}</h2>
            <div className="mt-6 grid gap-5 md:grid-cols-3">
              {next.map((n) => <GuideCard key={n.id} g={n} lang={lang} onClick={() => trackEvent("guide_related_click", { guide_id: n.id })} />)}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
